<template>
  <div id="app">
    <router-view v-slot="{ Component, route }">
      <component :is="Component" :key="route?.fullPath || 'default'" />
    </router-view>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import websocketService from '@/utils/websocket'

export default {
  name: 'App',
  setup() {
    const router = useRouter()
    const websocketStatus = ref('未连接')
    
    // 检查WebSocket服务是否正确导入
    console.log('WebSocket服务状态:', {
      service: websocketService,
      hasConnect: typeof websocketService?.connect === 'function',
      hasOnConnected: typeof websocketService?.onConnected === 'function'
    })
    
    // 检查是否为开发环境
    const isDevelopment = computed(() => {
      return process.env.NODE_ENV === 'development' || 
             window.location.hostname === 'localhost' ||
             window.location.hostname === '127.0.0.1'
    })
    
    // 将router实例设置到全局，供WebSocket服务使用
    window.$router = router
    
    // 处理告警通知
    const onAlarmNotification = (alarmNotification) => {
      console.log('App.vue收到告警通知:', alarmNotification)
      
      // 注意：不在这里显示弹窗，避免重复弹窗
      // 弹窗已由websocket.js中的订阅处理器处理
      
      // 播放告警音效
      playAlarmSound()
      
      // 发送浏览器通知
      sendBrowserNotification(alarmNotification)
      
      // 更新侧边栏告警计数
      if (window.updatePendingAlarmsCount) {
        console.log('更新告警计数...')
        // 延迟更新计数，给服务器时间处理
        setTimeout(() => {
          // 使用api工具发送请求，确保正确的基础URL和认证
          import('@/utils/api').then(api => {
            api.default.get('/alarms/pending-count')
              .then(data => {
                if (data && data.success) {
                  console.log('获取到最新告警计数:', data.data)
                  window.updatePendingAlarmsCount(data.data || 0)
                }
              })
              .catch(error => {
                console.error('更新告警计数失败:', error)
                // 如果获取失败，至少增加1个计数
                const currentCount = window.getPendingAlarmsCount ? window.getPendingAlarmsCount() : 0
                window.updatePendingAlarmsCount(currentCount + 1)
              })
          })
        }, 1500)
      }
    }
    
    // 播放告警音效
    const playAlarmSound = () => {
      try {
        const audio = new Audio('/sounds/alarm.mp3')
        audio.play().catch(error => {
          console.warn('播放告警音效失败:', error)
        })
      } catch (error) {
        console.warn('创建告警音效失败:', error)
      }
    }
    
    // 发送浏览器通知
    const sendBrowserNotification = (alarmNotification) => {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(alarmNotification.title || '新告警', {
          body: alarmNotification.content || '收到新的告警通知',
          icon: '/favicon.ico'
        })
      }
    }
    
    // 将WebSocket服务暴露到全局，方便调试
    window.websocketService = websocketService
    
    onMounted(() => {
      console.log('App.vue onMounted - 开始初始化WebSocket')
      
      // 检查用户是否已登录
      const userData = localStorage.getItem('user')
      console.log('从localStorage获取的用户数据:', userData)
      
      if (userData) {
        try {
          const user = JSON.parse(userData)
          console.log('解析后的用户对象:', user)
          
          if (user && user.id) {
            // 初始化WebSocket连接
            console.log('初始化WebSocket连接，用户ID:', user.id)
            websocketStatus.value = '连接中...'
            websocketService.connect(user.id)
            
            // 设置告警通知处理器
            websocketService.onAlarmNotification = onAlarmNotification
            
            // 设置连接状态处理器
            websocketService.onConnected = () => {
              console.log('WebSocket连接成功')
              websocketStatus.value = '已连接'
              // 可以显示连接状态或其他UI反馈
            }
            
            websocketService.onDisconnected = () => {
              console.log('WebSocket连接断开')
              websocketStatus.value = '已断开'
              // 可以显示断连提示
            }
            
            websocketService.onError = (error) => {
              console.error('WebSocket错误:', error)
              websocketStatus.value = '连接错误'
            }
          } else {
            console.warn('用户数据格式不正确:', user)
            websocketStatus.value = '用户数据错误'
          }
        } catch (error) {
          console.error('解析用户数据失败:', error)
          websocketStatus.value = '用户数据解析失败'
        }
      } else {
        console.warn('未找到用户数据，无法初始化WebSocket连接')
        websocketStatus.value = '未找到用户数据'
        
        // 尝试其他可能的键名
        const possibleKeys = ['user_data', 'userData', 'currentUser', 'authUser']
        for (const key of possibleKeys) {
          const data = localStorage.getItem(key)
          if (data) {
            console.log(`找到可能的用户数据键: ${key}`, data)
            try {
              const parsed = JSON.parse(data)
              if (parsed && (parsed.id || parsed.userId || parsed.user?.id)) {
                console.log('找到有效的用户数据:', parsed)
                websocketStatus.value = `找到数据在${key}中`
                break
              }
            } catch (e) {
              console.log(`键${key}的数据不是有效JSON`)
            }
          }
        }
      }
    })
    
    onUnmounted(() => {
      // 组件销毁时断开WebSocket连接
      websocketService.disconnect()
    })
    
    return {
      websocketStatus,
      isDevelopment
    }
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  height: 100vh;
}

body {
  margin: 0;
  padding: 0;
}

/* WebSocket告警通知样式 */
.alarm-notification {
  border-left: 4px solid #ff4d4f !important;
  box-shadow: 0 4px 12px rgba(255, 77, 79, 0.2) !important;
}

.alarm-notification.alarm-level-4 {
  border-left-color: #ff4d4f !important;
  background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%) !important;
}

.alarm-notification.alarm-level-3 {
  border-left-color: #fa8c16 !important;
  background: linear-gradient(135deg, #fff7e6 0%, #ffffff 100%) !important;
}

.alarm-notification.alarm-level-2 {
  border-left-color: #fadb14 !important;
  background: linear-gradient(135deg, #feffe6 0%, #ffffff 100%) !important;
}

.alarm-notification.alarm-level-1 {
  border-left-color: #52c41a !important;
  background: linear-gradient(135deg, #f6ffed 0%, #ffffff 100%) !important;
}
</style>